import { useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'

interface UseFocusOnErrorProps {
  fieldName: string
  fieldId: string
}

/**
 * Automatically focuses on the first invalid form field when Formik validation fails
 * @param fieldName - The Formik field name (e.g., 'email', 'patients[0].name')
 * @param fieldId - The DOM element ID to focus
 */
const useFocusOnError = ({ fieldName, fieldId }: UseFocusOnErrorProps): void => {
  const formik = useFormikContext<any>()
  const prevSubmitCountRef = useRef(formik.submitCount)

  useEffect(() => {
    // Only run if this is a new submission and form is invalid
    if (prevSubmitCountRef.current === formik.submitCount || formik.isValid) {
      return
    }

    prevSubmitCountRef.current = formik.submitCount

    // Get the first error field
    const errorKeys = Object.keys(formik.errors)
    if (errorKeys.length === 0) return

    const firstErrorKey = errorKeys[0]
    const firstError = formik.errors[firstErrorKey]

    // Handle array fields (e.g., patients[0].name)
    if (Array.isArray(firstError)) {
      const arrayErrors: string[] = []
      
      firstError.forEach((item, index) => {
        if (item && typeof item === 'object') {
          const nestedErrorKey = Object.keys(item)[0]
          if (nestedErrorKey) {
            arrayErrors.push(`${firstErrorKey}[${index}].${nestedErrorKey}`)
          }
        }
      })

      // Focus if this field matches the first array error
      if (arrayErrors.length > 0 && arrayErrors[0] === fieldName) {
        document.getElementById(fieldId)?.focus()
        return
      }
    }

    // Focus if this field matches the first simple error
    if (firstErrorKey === fieldName) {
      document.getElementById(fieldId)?.focus()
    }
  }, [formik.submitCount, formik.isValid, formik.errors, fieldName, fieldId])
}

export default useFocusOnError